// ============================================================================
//  HeroCTA — wide gold-glow glass hero call-to-action.
//
//  Replaces the legacy violet→teal gradient pill on the Guest Home /
//  Guest Dashboard surfaces so the most-important action sits in
//  the same cinematic visual language as the ProductCard grid below.
//
//  Per the user brief (May 2026):
//    "Most important CTA should remain Upload Moment. This can stay
//     wider/larger than the rest."
//
//  Visual rules:
//    • SAME dark glass surface as ProductCard, but wider and gold-tier
//    • Larger accent halo + soft ring on the icon chip
//    • Serif title (Georgia) + muted subtitle
//    • Loading shimmer when busy (no spinner — out of brand)
//    • Disabled state dims to 0.45 opacity, never blocks via cursor
//    • SAME spring motion (1.02 hover, 0.99 tap) for cohesion
//
//  Props:
//    title     — string, serif headline (defaults to "Share your moments")
//    subtitle  — string, emotional one-liner
//    icon      — React component (Phosphor / lucide icon)
//    accent    — semantic accent key ('gold' default; rarely overridden)
//    busy      — bool, shows the muted-shimmer copy & dims
//    busyLabel — override busy text
//    onClick   — handler
//    testId    — data-testid suffix
// ============================================================================
import React from 'react';
import { motion } from 'framer-motion';

const ACCENT = {
  gold:   { color: '#D4AF5E', glow: 'rgba(212,175,94,0.40)', icon: '#D4AF5E' },
  violet: { color: '#B89762', glow: 'rgba(184,151,98,0.40)', icon: '#A78BFA' },
  pink:   { color: '#FF6BCB', glow: 'rgba(255,107,203,0.40)', icon: '#FF6BCB' },
};

export const HeroCTA = ({
  title = 'Share your moments',
  subtitle = 'Add a moment to tonight',
  icon: Icon,
  accent = 'gold',
  busy = false,
  busyLabel = 'Uploading…',
  onClick,
  testId = 'hero-cta',
}) => {
  const a = ACCENT[accent] || ACCENT.gold;
  return (
    <motion.button
      type="button"
      data-testid={testId}
      onClick={onClick}
      disabled={busy}
      whileHover={busy ? {} : { y: -2, scale: 1.02 }}
      whileTap={busy ? {} : { scale: 0.99 }}
      transition={{ type: 'spring', stiffness: 320, damping: 22 }}
      style={{
        position: 'relative', overflow: 'hidden',
        display: 'flex', alignItems: 'center', gap: 16,
        width: '100%', padding: '20px 22px',
        borderRadius: 24,
        background: 'linear-gradient(180deg, rgba(28,24,42,0.96) 0%, rgba(18,16,28,0.96) 100%)',
        border: `1px solid ${a.color}3a`,                      // ~23% accent edge — louder than ProductCard
        boxShadow: `0 14px 38px rgba(0,0,0,0.45), inset 0 0 0 1px rgba(255,255,255,0.03), 0 0 36px ${a.glow}`,
        backdropFilter: 'blur(14px)',
        textAlign: 'left', color: 'inherit', cursor: busy ? 'wait' : 'pointer',
        opacity: busy ? 0.6 : 1,
        transition: 'opacity 0.25s',
      }}
    >
      {/* Accent halo — larger, more present than the ProductCard veil.
          This is what reads as "premium hero" without ever using a
          coloured fill. Bottom-right placement balances the icon top-left. */}
      <span
        aria-hidden
        style={{
          position: 'absolute', bottom: -50, right: -40,
          width: 180, height: 180, borderRadius: '50%',
          background: `radial-gradient(circle, ${a.color}33 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      {/* Icon — bigger than ProductCard (52×52 vs 40×40) so the hero
          feels distinctly larger at a glance, not just a wider card. */}
      {Icon ? (
        <span
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 52, height: 52, borderRadius: 14, flexShrink: 0,
            background: `${a.color}1f`,
            border: `1px solid ${a.color}55`,
            color: a.icon,
            filter: `drop-shadow(0 0 18px ${a.glow})`,
          }}
        >
          <Icon size={26} weight="bold" />
        </span>
      ) : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 0 }}>
        <span
          data-testid={`${testId}-title`}
          style={{
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontSize: 20, fontWeight: 500, color: '#FFFFFF',
            letterSpacing: '-0.02em', lineHeight: 1.15,
            textShadow: `0 0 20px ${a.glow}`,
          }}
        >
          {busy ? busyLabel : title}
        </span>
        <span
          style={{
            fontSize: 12, fontWeight: 500,
            color: 'rgba(255,255,255,0.6)',
            letterSpacing: '-0.005em',
          }}
        >
          {subtitle}
        </span>
      </div>

      {/* Tiny "→" affordance, accent-coloured, anchored right */}
      <span
        aria-hidden
        style={{
          fontSize: 18, fontWeight: 700, color: a.color,
          letterSpacing: '0.04em', flexShrink: 0,
          textShadow: `0 0 14px ${a.glow}`,
        }}
      >
        →
      </span>
    </motion.button>
  );
};

export default HeroCTA;
